/**
 * Role Service
 * Provides API methods for managing roles in the RBAC system
 */

import type { RoleDto, CreateRoleRequest, UpdateRoleRequest, RoleScope } from '@/types/roles';
import { authService } from './authService';
import { apiRequest } from '@/config/msalConfig';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface ApiErrorResponse {
  message: string;
  details?: string;
}

class ApiError extends Error {
  response: ApiErrorResponse;
  status: number;

  constructor(response: ApiErrorResponse, status: number) {
    super(response.message);
    this.name = 'ApiError';
    this.response = response;
    this.status = status;
  }
}

async function getDefaultHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const baseHeaders: Record<string, string> = { Accept: 'application/json' };

  // Try to get Bearer token from MSAL if authenticated
  if (authService.isAuthenticated()) {
    try {
      // Request API scope for backend authorization
      const token = await authService.getAccessToken(apiRequest.scopes);
      baseHeaders['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.warn('Failed to get access token for roles request:', error);
    }
  }

  return { ...baseHeaders, ...extra };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      try {
        const errorData = (await response.json()) as ApiErrorResponse;
        throw new ApiError(errorData, response.status);
      } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new ApiError(
          {
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: 'Malformed JSON error response',
          },
          response.status
        );
      }
    }

    throw new ApiError(
      {
        message: `HTTP ${response.status}: ${response.statusText}`,
        details: 'No JSON error body returned',
      },
      response.status
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return await response.json();
}

export class RoleService {
  /**
   * Get all active roles
   * Requires: Viewer role or higher
   */
  async getRoles(): Promise<RoleDto[]> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles`, { headers });
    return handleResponse<RoleDto[]>(response);
  }

  /**
   * Get all roles including inactive ones
   * Requires: Administrator role
   */
  async getAllRoles(): Promise<RoleDto[]> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/all`, { headers });
    return handleResponse<RoleDto[]>(response);
  }

  /**
   * Get a specific role by ID
   * Requires: Viewer role or higher
   */
  async getRoleById(id: number): Promise<RoleDto> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/${id}`, { headers });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Get a specific role by name
   * Requires: Viewer role or higher
   */
  async getRoleByName(name: string): Promise<RoleDto> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/by-name/${encodeURIComponent(name)}`, {
      headers,
    });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Create a new role
   * Requires: Administrator role
   */
  async createRole(request: CreateRoleRequest): Promise<RoleDto> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/roles`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Update an existing role
   * Requires: Administrator role
   */
  async updateRole(id: number, request: UpdateRoleRequest): Promise<RoleDto> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE}/roles/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Deactivate a role (soft delete)
   * Requires: Administrator role
   * Note: Core roles (IDs 1-8) cannot be deactivated
   */
  async deleteRole(id: number): Promise<RoleDto> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/${id}`, {
      method: 'DELETE',
      headers,
    });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Activate a previously deactivated role
   * Requires: Administrator role
   */
  async activateRole(id: number): Promise<RoleDto> {
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/${id}/activate`, {
      method: 'POST',
      headers,
    });
    return handleResponse<RoleDto>(response);
  }

  /**
   * Get combined scopes from multiple roles
   * Useful for evaluating permissions for users with multiple roles
   * Requires: Viewer role or higher
   */
  async getCombinedScopes(roleIds: number[]): Promise<RoleScope[]> {
    const roleIdsParam = roleIds.join(',');
    const headers = await getDefaultHeaders();
    const response = await fetch(`${API_BASE}/roles/combined-scopes?roleIds=${roleIdsParam}`, {
      headers,
    });
    return handleResponse<RoleScope[]>(response);
  }
}

// Export singleton instance
export const roleService = new RoleService();
